import React from 'react';
import styled from 'styled-components';
import Section from '../../Section.js';
import Breadcrumbs from '../../Breadcrumbs.js';
import { colors } from '../../../styles/colors.ts';
import { Tabs, Tab } from '../../Tabs.js';
import Modal from '../../Modal.js';
import ScrollToTop from '../../ScrollToTop.js';
import { TitleSection, ProjectContent, ConclusionSection, HeroImage, HighlightedParagraph } from './Layout.js';
import Carrousel, { CarouselSection } from '../../Carrousel.js';
import BeforeAfter from '../../BeforeAfter.js';
import ReactDOM from 'react-dom';
import investifiData from './data/investifi.json';
import usePageMeta from '../../../hooks/usePageMeta';


const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6rem;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4rem;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4rem;
  width: 100%;

  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const HeaderTopbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Logo = styled.img`
  height: 2.5rem;
  width: auto;
  pointer-events: none;
`;

const Year = styled.span`
  font-size: 0.8rem;
  color: ${colors.neutral[400]};
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.neutral[200]};
`;

const Investifi = () => {
  usePageMeta({
    title: `Guzmán Cura | InvestiFi Case Study`,
    description: investifiData.header.description.subtitle,
    keywords: ['InvestiFi', 'investment platform', 'UX design', 'UI design', 'fintech', 'user flows', 'case study', 'Guzmán Cura'],
  });

  const [selectedImage, setSelectedImage] = React.useState(null);

  const handleImageClick = (image) => {
    setSelectedImage({
      src: image.src,
      alt: image.alt,
      image
    });
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  const renderModal = () => {
    if (!selectedImage) return null;
    
    return ReactDOM.createPortal(
      <Modal 
        image={selectedImage}
        onClose={handleClose}
        zoomLevel={200}
      />,
      document.body
    );
  };

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Investifi', path: '/projects/investifi' }
  ];

  return (
    <Section>
      <ScrollToTop />
      <Container>
        <HeaderSection>
          <HeaderTopbar>
            <Breadcrumbs items={breadcrumbItems} />
            <Logo src={investifiData.header.logo} alt="InvestiFi Logo" />
            <Year>{investifiData.header.year}</Year>
          </HeaderTopbar>
          <HeroImage 
            src={investifiData.header.heroImage.desktop}
            mobileSrc={investifiData.header.heroImage.mobile}
            alt={investifiData.header.heroImage.alt}
            onClick={(e) => handleImageClick(e.target)}
          />
          <HighlightedParagraph>
            <h2>{investifiData.header.description.title}</h2>
            <h3>{investifiData.header.description.subtitle}</h3>
          </HighlightedParagraph>
          <Divider />
        </HeaderSection>
        <Tabs stretch align="center">
          <Tab label="User flows">
            <ContentWrapper>
              <Carrousel 
                  title={investifiData.overview.userFlows.investmentFlow.title}
                  images={investifiData.overview.userFlows.investmentFlow.images}
                  label={investifiData.overview.userFlows.investmentFlow.labels}
                  onImageClick={handleImageClick}
                  aspectRatio="16/9"
                  mobileContain={true}
                  condensed
                  stretchControls
                  height="fit-content"
                />
              <Divider />
              <CarouselSection>  
                <Carrousel 
                  title={investifiData.overview.userFlows.search.title}
                  images={investifiData.overview.userFlows.search.images}
                  label={investifiData.overview.userFlows.search.labels}
                  onImageClick={handleImageClick}
                  condensed
                  height="fit-content"
                  mobileHeight="480px"
                  contain={true}
                  mobileContain={true}
                  maxWidth="280px"
                />
                <Carrousel 
                  title={investifiData.overview.userFlows.onboarding.title}
                  images={investifiData.overview.userFlows.onboarding.images}
                  label={investifiData.overview.userFlows.onboarding.labels}
                  onImageClick={handleImageClick}
                  condensed
                  height="fit-content"
                  mobileHeight="480px"
                  contain={true}
                  mobileContain={true}
                  maxWidth="280px"
                />
                <Carrousel 
                  title={investifiData.overview.userFlows.otherScreens.title}
                  images={investifiData.overview.userFlows.otherScreens.images}
                  label={investifiData.overview.userFlows.otherScreens.labels}
                  onImageClick={handleImageClick}
                  condensed
                  height="fit-content"
                  mobileHeight="480px"
                  contain={true}
                  mobileContain={true}
                  maxWidth="280px"
                />
              </CarouselSection>
            </ContentWrapper>
          </Tab>
          <Tab label="Case Study">
            <ContentWrapper>
              <TitleSection 
                title={investifiData.caseStudy.title}
                tags={investifiData.caseStudy.tags}
                description={investifiData.caseStudy.description}
              />
              
              {investifiData.caseStudy.sections.map((section, index) => (
                section.type === 'beforeAfter' ? (
                  <ProjectContent 
                    key={index}
                    image={
                      <BeforeAfter 
                        beforeImage={section.beforeImage}
                        afterImage={section.afterImage}
                        beforeMobile={section.beforeMobile}
                        afterMobile={section.afterMobile}
                        height={section.height}
                        mobileHeight={section.mobileHeight}
                      />
                    }
                    wide={section.wide}
                  />
                ) : (
                  <ProjectContent 
                    key={index}
                    {...section}
                  />
                )
              ))}

              <Divider /> 
              <ConclusionSection 
                title={investifiData.caseStudy.outcome.title}
                subtitle={investifiData.caseStudy.outcome.subtitle}
              />
              <Logo src={investifiData.header.logo} alt="InvestiFi Logo" />

              {/* <Carrousel 
                images={investifiData.overview.gallery.images}
                onImageClick={handleImageClick}
              /> */}
            </ContentWrapper>
          </Tab>
        </Tabs>
      </Container>
      {renderModal()}
    </Section>
  );
};

export default Investifi;